import { createContext, PropsWithChildren, useContext } from 'react';
import { QueryFactory } from 'services/api';
import { ProjectDto } from 'services/api/api-client.types.ts';
import { RequireExactlyOne } from 'type-fest';
import { useHandleProjectAccessDenied } from '../../components/header/useHandleProjectAccessDenied.ts';

type ProjectContextData = Pick<
  ProjectDto,
  'currentUserPermissions' | 'isPrivate' | 'name'
>;

export type ProjectProviderProps = RequireExactlyOne<{
  projectId: number;
  data: ProjectContextData;
}>;

export const ProjectProvider = (
  props: ProjectProviderProps & PropsWithChildren,
) => {
  const projectQuery = QueryFactory.ProjectQuery.useGetQuery(props.projectId!, {
    enabled: props.data === null,
    throwOnError: false,
  });
  useHandleProjectAccessDenied({ projectQuery, projectId: props.projectId });

  return (
    <ProjectContext.Provider
      value={{ project: props.data ?? projectQuery.data ?? null }}
    >
      {props.children}
    </ProjectContext.Provider>
  );
};

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (!context)
    throw new Error('useProjectContext must be used within a ProjectProvider');

  return context;
};

export type ProjectContextType = {
  project: ProjectContextData | null;
};

export const ProjectContext = createContext<ProjectContextType | null>(null);
